import {
  useEffect,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from "react";
import {
  Dimensions,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  Text,
  View,
  type ViewStyle,
} from "react-native";
import { Check, type LucideIcon } from "lucide-react-native";
import { createPortal } from "react-dom";
import {
  colors,
  radii,
  semanticColors,
  shadows,
  typography,
} from "../theme/tokens";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";

/**
 * The opaque menu panel the select menu floats in. Exported so inline
 * suggestion lists under a text field (where a floating menu would steal
 * focus from typing) can use the same surface.
 */
export const selectMenuSurfaceStyle: ViewStyle = {
  overflow: "hidden",
  borderRadius: radii.md,
  borderWidth: 1,
  borderColor: "rgba(42, 37, 34, 0.12)",
  // Intentionally opaque. This menu sits over dense form content,
  // where a glass surface makes labels appear to bleed through.
  backgroundColor: colors.surface,
  ...shadows.md,
  ...(Platform.OS === "web"
    ? ({
        boxShadow: "0 12px 30px rgba(42, 37, 34, 0.18)",
      } as ViewStyle)
    : null),
};

/** One option row inside `selectMenuSurfaceStyle`. */
export function selectMenuItemStyle(pressed: boolean): ViewStyle {
  return {
    minHeight: 44,
    flexDirection: "row",
    alignItems: "center",
    gap: 9,
    paddingVertical: 10,
    paddingHorizontal: 14,
    backgroundColor: pressed ? "rgba(42, 37, 34, 0.06)" : colors.surface,
  };
}

export type AnchoredSelectMenuItem = {
  value: string;
  label: string;
  icon?: LucideIcon;
  muted?: boolean;
  separatorBefore?: boolean;
  /** Stable analytics identifier, required when `label` is translated. */
  action?: string;
};

type Props = {
  visible: boolean;
  triggerRef: RefObject<View | null>;
  value: string | null;
  items: AnchoredSelectMenuItem[];
  onSelect: (value: string) => void;
  onClose: () => void;
  align?: "left" | "right";
  width?: number;
  maxHeight?: number;
  accentColor?: string;
  /** Let a containing Dialog own Escape so one key press cannot dismiss both layers. */
  captureEscape?: boolean;
  testID?: string;
};

type Anchor = {
  x: number;
  y: number;
  width: number;
  height: number;
};

function WebMenuPortal({ children }: { children: ReactNode }) {
  const [mounted, setMounted] = useState(false);
  const portalRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const portal = document.createElement("div");
    portal.style.position = "fixed";
    portal.style.inset = "0";
    portal.style.width = "100%";
    portal.style.height = "100%";
    portal.style.zIndex = "2147483647";
    portal.style.pointerEvents = "auto";
    portal.dataset.moonAnchoredMenuPortal = "true";
    document.body.appendChild(portal);
    portalRef.current = portal;
    setMounted(true);

    return () => {
      portal.remove();
      portalRef.current = null;
    };
  }, []);

  if (!mounted || !portalRef.current) return null;

  return createPortal(children, portalRef.current);
}

/**
 * A selection menu rendered in its own modal layer.
 *
 * Unlike an absolutely positioned menu inside a ScrollView, this surface is
 * not clipped by the dialog or interleaved with later form controls. The
 * full-screen press target also prevents an outside click from activating the
 * control underneath while dismissing the menu.
 */
export function AnchoredSelectMenu({
  visible,
  triggerRef,
  value,
  items,
  onSelect,
  onClose,
  align = "left",
  width,
  maxHeight = 320,
  accentColor = colors.deepBrown,
  captureEscape = true,
  testID,
}: Props) {
  const capturePress = usePressCapture("AnchoredSelectMenu");
  const [anchor, setAnchor] = useState<Anchor | null>(null);

  useEffect(() => {
    if (!visible) {
      setAnchor(null);
      return;
    }

    triggerRef.current?.measureInWindow((x, y, triggerWidth, triggerHeight) => {
      setAnchor({ x, y, width: triggerWidth, height: triggerHeight });
    });
  }, [triggerRef, visible]);

  useEffect(() => {
    if (Platform.OS !== "web" || !visible || !captureEscape) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      onClose();
    };

    // Capture on window before React Native Web's outer Modal sees Escape;
    // only the topmost picker should close on the first key press.
    window.addEventListener("keydown", handleKeyDown, true);
    return () => window.removeEventListener("keydown", handleKeyDown, true);
  }, [captureEscape, onClose, visible]);

  const screen = Dimensions.get("window");
  const menuWidth = anchor
    ? Math.min(width ?? Math.max(anchor.width, 180), screen.width - 16)
    : (width ?? 180);
  const estimatedHeight = Math.min(maxHeight, items.length * 45);
  const belowTop = anchor ? anchor.y + anchor.height + 6 : 8;
  const menuTop = anchor
    ? screen.height - belowTop - 8 >= estimatedHeight
      ? belowTop
      : Math.max(8, anchor.y - estimatedHeight - 6)
    : 8;
  const horizontalPosition = anchor
    ? align === "right"
      ? { right: Math.max(8, screen.width - (anchor.x + anchor.width)) }
      : { left: Math.max(8, Math.min(anchor.x, screen.width - menuWidth - 8)) }
    : { left: 8 };

  const menuLayer = (
    <Pressable
      testID={testID ? `${testID}-backdrop` : undefined}
      accessibilityRole="button"
      accessibilityLabel="Close selection menu"
      onPress={onClose}
      style={{
        position: Platform.OS === "web" ? ("fixed" as "absolute") : "absolute",
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
      }}
    >
      {anchor ? (
        <Pressable
          testID={testID}
          accessibilityRole="menu"
          onPress={(event) => event.stopPropagation()}
          style={{
            position: "absolute",
            top: menuTop,
            ...horizontalPosition,
            width: menuWidth,
            maxHeight,
            ...selectMenuSurfaceStyle,
          }}
        >
          <ScrollView
            showsVerticalScrollIndicator={false}
            keyboardShouldPersistTaps="handled"
          >
            {items.map((item) => {
              const selected = item.value === value;
              const Icon = item.icon;
              const itemColor = item.muted
                ? colors.textMid
                : selected
                  ? accentColor
                  : colors.text;

              return (
                <Pressable
                  key={`${item.value}-${item.label}`}
                  accessibilityRole="menuitem"
                  accessibilityLabel={item.label}
                  accessibilityState={{ selected }}
                  onPress={() => {
                    capturePress(
                      pressLabel(item.label),
                      item.action ?? item.value,
                    );
                    onSelect(item.value);
                  }}
                  style={({ pressed }) => ({
                    minHeight: 44,
                    flexDirection: "row",
                    alignItems: "center",
                    gap: 9,
                    paddingVertical: 10,
                    paddingHorizontal: 14,
                    borderTopWidth: item.separatorBefore ? 1 : 0,
                    borderTopColor: "rgba(42, 37, 34, 0.08)",
                    backgroundColor: pressed
                      ? "rgba(42, 37, 34, 0.06)"
                      : selected
                        ? semanticColors.accent.tint
                        : colors.surface,
                  })}
                >
                  {Icon ? (
                    <Icon size={15} color={itemColor} strokeWidth={1.7} />
                  ) : null}
                  <Text
                    className="font-dm-sans"
                    style={{
                      flex: 1,
                      fontFamily: typography.families.sans,
                      fontSize: 14,
                      fontWeight: selected ? "600" : "400",
                      color: itemColor,
                    }}
                  >
                    {item.label}
                  </Text>
                  {selected ? (
                    <Check size={15} color={accentColor} strokeWidth={2} />
                  ) : null}
                </Pressable>
              );
            })}
          </ScrollView>
        </Pressable>
      ) : null}
    </Pressable>
  );

  if (Platform.OS === "web") {
    return visible ? <WebMenuPortal>{menuLayer}</WebMenuPortal> : null;
  }

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onClose}
    >
      {menuLayer}
    </Modal>
  );
}
